<template>
  <div class="tenant-domain-manager">
    <!-- 页面标题 + 返回 -->
    <ContentWrap>
      <div class="page-header">
        <el-button link @click="handleBack">
          <Icon icon="ep:arrow-left" class="mr-5px" /> 返回租户列表
        </el-button>
        <span class="page-title">域名管理 — 租户 #{{ tenantId }}</span>
      </div>
    </ContentWrap>

    <!-- 操作栏 -->
    <ContentWrap>
      <el-form :inline="true" class="demo-form-inline">
        <el-form-item>
          <el-button
            type="primary"
            plain
            @click="openForm('create')"
            v-hasPermi="['system:tenant-domain:create']"
          >
            <Icon icon="ep:plus" class="mr-5px" /> 新增绑定
          </el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <!-- 列表 -->
    <ContentWrap>
      <el-table
        v-loading="loading"
        :data="list"
        :stripe="true"
        :show-overflow-tooltip="true"
      >
        <el-table-column label="域名/AppID" prop="domain" min-width="200" />
        <el-table-column label="类型" width="120" align="center">
          <template #default="{ row }">
            <el-tag :type="getDomainTypeTag(row.domainType)" size="small">
              {{ getDomainTypeLabel(row.domainType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="关联模块" width="120" align="center">
          <template #default="{ row }">
            <span>{{ row.moduleCode || '通用' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="关联公众号" min-width="140" align="center">
          <template #default="{ row }">
            <span>{{ formatMpAccount(row.mpAccountId) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="是否默认" width="100" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.isDefault" type="success" size="small">默认</el-tag>
            <el-tag v-else type="info" size="small">否</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170" align="center">
          <template #default="{ row }">
            <span>{{ formatDate(row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right" align="center">
          <template #default="{ row }">
            <el-button
              link
              type="primary"
              @click="openForm('update', row.id)"
              v-hasPermi="['system:tenant-domain:update']"
            >
              编辑
            </el-button>
            <el-button
              link
              type="danger"
              @click="handleDelete(row.id)"
              v-hasPermi="['system:tenant-domain:delete']"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <!-- 分页 -->
      <Pagination
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>

    <!-- 表单弹窗 -->
    <DomainForm ref="formRef" @success="getList" />
  </div>
</template>

<script lang="ts" setup>
import * as TenantDomainApi from '@/api/system/tenantDomain/index'
import * as MpAccountApi from '@/api/mp/account'
import DomainForm from './DomainForm.vue'
import { formatDate } from '@/utils/formatTime'
import { useRouter, useRoute } from 'vue-router'

defineOptions({ name: 'SystemTenantDomain' })

const router = useRouter()
const route = useRoute()
const loading = ref(true)
const list = ref<TenantDomainApi.TenantDomainVO[]>([])
const total = ref(0)
const formRef = ref()
const mpAccountMap = ref<Record<number, string>>({})

const tenantId = ref<number>(Number(route.query.tenantId) || 0)

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20
})

/** 域名类型标签 */
const domainTypeLabelMap: Record<number, string> = {
  1: 'H5域名',
  2: '小程序AppID',
  3: 'App包名',
  4: '管理后台域名'
}
const getDomainTypeLabel = (type?: number) => {
  return type ? domainTypeLabelMap[type] || '未知' : '未知'
}
const getDomainTypeTag = (type?: number) => {
  const map: Record<number, string> = {
    1: '',
    2: 'success',
    3: 'warning',
    4: 'danger'
  }
  return type ? map[type] || 'info' : 'info'
}

const formatMpAccount = (mpAccountId?: number) => {
  if (!mpAccountId) {
    return '—'
  }
  return mpAccountMap.value[mpAccountId] || `#${mpAccountId}`
}

const loadMpAccounts = async () => {
  try {
    const accounts = await MpAccountApi.getSimpleAccountList()
    const map: Record<number, string> = {}
    ;(accounts || []).forEach((acc: MpAccountApi.AccountVO) => {
      map[acc.id] = acc.name || `#${acc.id}`
    })
    mpAccountMap.value = map
  } catch {
    mpAccountMap.value = {}
  }
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await TenantDomainApi.getTenantDomainPage({
      ...queryParams,
      tenantId: tenantId.value
    })
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 打开表单 */
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, tenantId.value, id)
}

/** 删除 */
const handleDelete = async (id: number) => {
  try {
    await ElMessageBox.confirm('确认删除该域名绑定？', '提示', { type: 'warning' })
    await TenantDomainApi.deleteTenantDomain(id)
    ElMessage.success('删除成功')
    getList()
  } catch {
    // 取消操作
  }
}

/** 返回租户列表 */
const handleBack = () => {
  router.push({ path: '/system/tenant/list' })
}

onMounted(async () => {
  await loadMpAccounts()
  getList()
})
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.page-title {
  font-size: 16px;
  font-weight: 600;
}
</style>
